import { Injectable } from '@angular/core';
import { ProductRepository } from '@skylab-angular-shared/business';
import { CommunityTransactionForSaleQuery, ContextKeys, VatRateForSaleBaseQuery, VatRateForSaleQuery, VatRateForSaleWstoQuery, VatRateItem, sortVatRates } from '@skylab-shared/business';
import { BusinessObjectBase, IdentifiableValueConverter, LocalContext, ModelBase, ObservableCollection, RepositoryBase } from '@skylab-shared/core';
import { DocumentItemModel, ProductModel } from '@skylab-shared/dataModel';
import { FormBase, FormBaseViewModel } from '../form/form-base';

export type DocumentFormViewModelOssVatRatesItemsExtension = {
    vatRateConverter: IdentifiableValueConverter;
    vatRates: {
        value: VatRateItem[];
    };
};

interface DocumentModel extends ModelBase {
    communityTransactionId: number;
    wstoConsumerCountryId: number | null;
    items: ObservableCollection<DocumentItemModel>;
}

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelOssVatRatesItemsExtensionService {
    public constructor(
        private readonly productRepository: ProductRepository
    ) {}

    public extendViewModel<TViewModelPart, TViewModel extends TViewModelPart & FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): TViewModelPart & DocumentFormViewModelOssVatRatesItemsExtension {
        return Object.assign(viewModel as any, this.getViewModelExtension(formComponent, localContext));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & DocumentModel>(
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext): DocumentFormViewModelOssVatRatesItemsExtension {
        const model = formComponent.bo.model;
        const self = this;
        const vatRates = localContext.getPreloaded(VatRateForSaleQuery) || [];
        let internalVatRates: VatRateItem[] = [];
        const setVatRates = () => internalVatRates = sortVatRates(self.getVatRates(localContext, model));
        setVatRates();

        formComponent.registerListening(model.onPropertyChanged(
            async ({ propertyName }) => {
                if (propertyName === 'wstoConsumerCountryId') {
                    setVatRates();
                    await Promise.all(model.items.map(async item => {
                        await this.updateItemVatRateAsync(item, localContext, model);
                    }));
                } else if (propertyName === 'communityTransactionId') {
                    setVatRates();
                }
            }
        ));

        const viewModelExtension: DocumentFormViewModelOssVatRatesItemsExtension = {
            vatRateConverter: new IdentifiableValueConverter(vatRates),
            vatRates: {
                get value() {
                    return internalVatRates;
                }
            }
        };

        return viewModelExtension;
    }

    private getVatRates(localContext: LocalContext, model: DocumentModel): VatRateItem[] {
        const isWstoOssDocument = localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WSTO-OSS' })?.id === model.communityTransactionId;
        if (!isWstoOssDocument) return (localContext.getPreloaded(VatRateForSaleBaseQuery) || []).filter(v => v.communityTransactions.indexOf(model.communityTransactionId) > -1);
        if (isWstoOssDocument && model.wstoConsumerCountryId) {
            const usedVatRatesIds: number[] = localContext.getPreloaded(ContextKeys.vatRatesUsedInWstoDocument(model.sun)) || [];
            const vatRates = localContext.filterPreloaded(VatRateForSaleWstoQuery, { countryId: model.wstoConsumerCountryId }) || [];

            return vatRates.filter(elem => usedVatRatesIds.includes(elem.id) || elem.active);
        }

        return [];
    }

    private async updateItemVatRateAsync(item: DocumentItemModel, localContext: LocalContext, model: DocumentModel): Promise<void> {
        const product = item.productId ? await localContext.getAsync(ContextKeys.product(item.productId), async () => {
            const p = await this.productRepository.findAsync(item.productId!);
            if (!p) {
                throw new Error('Product not found');
            }
            return p;
        }) : new ProductModel();
        const ossVatRates = localContext.filterPreloaded(VatRateForSaleWstoQuery, { active: true }) || [];
        const vatRateOnProduct = ossVatRates.find(elem => elem.id === product.ossVatRates.find(elem => elem.countryId === model.wstoConsumerCountryId)?.vatRateId);
        if (vatRateOnProduct?.active) {
            item.vatRateId = vatRateOnProduct.id;
        }
    }
}
